import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Divider from '@hero/ui-kit/components/Divider';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import Filter from '../Filter';
import FilterOptionRow from '../FilterPanel/FilterOptionRow';
import FilterPillGroup from '../FilterPanel/FilterPillGroup';
import { FilterChip, usePublishFilterChips } from '../FilterPanel/FilterChipsContext';
import useOrgStorageKey from '../../hooks/useOrgStorageKey';
import * as Style from './style.module.scss';

// Hoisted so the default keeps a stable identity. A fresh `[]` per render invalidates the
// toggle callbacks below, and with them the `chips` memo they feed — which re-fires the chip
// publisher on every render and ping-pongs the chips provider (see usePublishFilterChips).
const NO_PAGINATION_KEYS: string[] = [];

export interface ListFilterOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
    dividerAfter?: boolean;
    // For the 'pills' variant: custom fill (and optional text colour) when the
    // pill is active — e.g. the Stage facet's pastel status colours.
    activeColor?: string;
    activeTextColor?: string;
}

interface ListFilterProps {
    options: ListFilterOption[];
    searchParam: string;
    label: string;
    mode?: 'multi' | 'single';
    hasResetBtn?: boolean;
    width?: string;
    persistToLocalStorage?: boolean;
    resetPaginationKeys?: string[];
    // Applied as a READ-TIME fallback for display when the URL param is absent
    // (never auto-written to the URL). The actual backend query applies its own
    // fallback in useStatesQueryParams. Show-All writes the full option set.
    defaultValues?: string[];
    renderContent?: (
        options: ListFilterOption[],
        selectedValues: string[],
        onToggle: (value: string) => void,
        onReset: () => void
    ) => React.ReactNode;
    formatLabel?: (selectedValues: string[], options: ListFilterOption[]) => string;
    disabled?: boolean;
    inline?: boolean;
    // 'rows' (default) renders checkbox/radio rows; 'pills' renders toggleable
    // pills (like the date-range presets). Multi-select shows multiple active pills.
    variant?: 'rows' | 'pills';
}

const ListFilter: React.FC<ListFilterProps> = ({
    options,
    searchParam,
    label,
    mode = 'multi',
    hasResetBtn = false,
    width = '24rem',
    persistToLocalStorage = false,
    resetPaginationKeys = NO_PAGINATION_KEYS,
    defaultValues,
    renderContent,
    formatLabel,
    disabled = false,
    inline = false,
    variant = 'rows'
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm] = React.useState('');
    const rawValue = searchParams.get(searchParam);
    // Persisted selection is per-organization: namespace the localStorage key by
    // the active org (the URL param name itself stays `searchParam`).
    const orgStorageKey = useOrgStorageKey();
    const storageKey = orgStorageKey(searchParam);

    React.useEffect(() => {
        // The URL param already drives the filter — nothing to restore.
        if (rawValue !== null) {
            return;
        }

        // Restore a previously persisted selection, sanitized against the
        // current option set so stale/retired keys (e.g. QUALIFIED) never
        // re-enter the URL. Defaults are NOT seeded here — they are applied as
        // a read-time fallback (see `selectedValues` + useStatesQueryParams),
        // which keeps the explicit Show-All state distinct from "absent".
        if (persistToLocalStorage) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const valid = new Set(options.map((o) => o.value));
                const cleaned = stored
                    .split(',')
                    .filter((v) => v && valid.has(v))
                    .join(',');

                if (cleaned) {
                    setSearchParams(
                        (prev) => {
                            prev.set(searchParam, cleaned);
                            return prev;
                        },
                        { replace: true }
                    );
                } else {
                    localStorage.removeItem(storageKey);
                }
            }
        }
    }, [searchParam, storageKey, setSearchParams, persistToLocalStorage, rawValue, options]);

    const selectedValues = React.useMemo(() => {
        if (rawValue !== null) {
            const valid = new Set(options.map((o) => o.value));
            return rawValue.split(',').filter((v) => v && valid.has(v));
        }
        // Absent param → show the defaults as preselected (display only).
        return defaultValues ?? [];
    }, [rawValue, defaultValues, options]);

    const handleMultiToggle = React.useCallback(
        (value: string) => {
            setSearchParams((prev) => {
                // Base the toggle on the EFFECTIVE selection (which includes the
                // default fallback when the param is absent) — not the raw param.
                // Otherwise unchecking a default-shown item would wrongly add it.
                const current = [...selectedValues];
                const index = current.indexOf(value);

                if (index < 0) {
                    current.push(value);
                } else {
                    current.splice(index, 1);
                }

                const paramString = current.filter((i) => !!i).join(',');

                if (paramString) {
                    prev.set(searchParam, paramString);

                    if (persistToLocalStorage) {
                        localStorage.setItem(storageKey, paramString);
                    }
                } else {
                    prev.delete(searchParam);

                    if (persistToLocalStorage) {
                        localStorage.removeItem(storageKey);
                    }
                }

                resetPaginationKeys.forEach((key) => prev.delete(key));

                return prev;
            });
        },
        [
            setSearchParams,
            searchParam,
            storageKey,
            selectedValues,
            persistToLocalStorage,
            resetPaginationKeys
        ]
    );

    const handleSingleToggle = React.useCallback(
        (value: string) => {
            setSearchParams((prev) => {
                if (prev.get(searchParam) === value) {
                    prev.delete(searchParam);
                } else {
                    prev.set(searchParam, value);
                }

                resetPaginationKeys.forEach((key) => prev.delete(key));

                return prev;
            });
        },
        [setSearchParams, searchParam, resetPaginationKeys]
    );

    // "Clear" / reset — simply drops this filter's param from the URL (rather
    // than writing out every option), which clears the facet, and resets
    // pagination so results start from the first page.
    const handleReset = React.useCallback(() => {
        setSearchParams((prev) => {
            prev.delete(searchParam);

            if (persistToLocalStorage) {
                localStorage.removeItem(storageKey);
            }

            resetPaginationKeys.forEach((key) => prev.delete(key));

            return prev;
        });
    }, [searchParam, storageKey, setSearchParams, persistToLocalStorage, resetPaginationKeys]);

    const filterLabel = React.useMemo(() => {
        if (formatLabel) {
            return formatLabel(selectedValues, options);
        }

        if (mode === 'single' && rawValue) {
            const option = options.find((o) => o.value === rawValue);
            return `${label}: ${option ? option.label : rawValue}`;
        }

        if (mode === 'multi' && selectedValues.length) {
            const names = selectedValues
                .map((v) => options.find((o) => o.value === v)?.label)
                .filter(Boolean)
                .join(', ');
            return names || `${label}: All`;
        }

        return `${label}: All`;
    }, [mode, rawValue, selectedValues, options, label, formatLabel]);

    const handleToggle = mode === 'multi' ? handleMultiToggle : handleSingleToggle;

    // Publish the active selections as removable chips. Only when the param is
    // explicitly set (matching the badge count) — a default-only display never
    // surfaces chips. Removing a chip toggles that value off.
    const chips = React.useMemo<FilterChip[]>(() => {
        if (rawValue === null) {
            return [];
        }
        return selectedValues.map((value) => ({
            key: `${searchParam}:${value}`,
            label: options.find((o) => o.value === value)?.label ?? value,
            onRemove: () => handleToggle(value)
        }));
    }, [rawValue, selectedValues, options, searchParam, handleToggle]);
    usePublishFilterChips(searchParam, chips);

    const filteredOptions = React.useMemo(() => {
        if (!searchTerm) return options;
        const term = searchTerm.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(term));
    }, [options, searchTerm]);

    return (
        <Filter
            hasFilters={typeof rawValue === 'string'}
            filterLabel={filterLabel}
            width={width}
            disabled={disabled}
            inline={inline}
            heading={label}
            count={rawValue !== null ? selectedValues.length : 0}
            onShowAll={hasResetBtn && inline ? handleReset : undefined}
        >
            {renderContent ? (
                renderContent(filteredOptions, selectedValues, handleToggle, handleReset)
            ) : variant === 'pills' ? (
                <FilterPillGroup
                    options={filteredOptions.map((option) => ({
                        value: option.value,
                        label: option.label,
                        activeColor: option.activeColor,
                        activeTextColor: option.activeTextColor
                    }))}
                    selectedValues={selectedValues}
                    onToggle={handleToggle}
                />
            ) : (
                <div className={Style.filterWrapper}>
                    {filteredOptions.map((option) => (
                        <React.Fragment key={option.value}>
                            <FilterOptionRow
                                label={option.label}
                                mode={mode}
                                selected={selectedValues.includes(option.value)}
                                onToggle={() => handleToggle(option.value)}
                                trailing={option.icon || null}
                            />
                            {option.dividerAfter ? (
                                <Divider styles={{ margin: '0 1.6rem' }} />
                            ) : null}
                        </React.Fragment>
                    ))}
                </div>
            )}
            {hasResetBtn && !inline && rawValue !== null ? (
                <>
                    <Divider styles={{ margin: '0 calc(var(--gutter) / 2)' }} />
                    <Section centered noDefaultPadding>
                        <Button
                            variant="primary"
                            noDefaultMargin
                            width="full"
                            className={Style.resetBtn}
                            onClick={handleReset}
                        >
                            Clear
                        </Button>
                    </Section>
                </>
            ) : null}
        </Filter>
    );
};

export default ListFilter;
